import type { Poll } from 'schema/src/types/poll.generated';
import type { ICState } from 'schema/src/types/state.generated';
import { getQuestionGroupId, selectGroup, selectGroupQuestions } from './selects';

export function getQuestionIdsForNavigation(
  data: Poll,
  icState: ICState,
  { withSequentialDisplay }: { withSequentialDisplay?: boolean } = {},
): string[] {
  const questionPositionMap = mapQuestionPositions(icState.questionsIndices);
  const setQuestionsMap = mapSetQuestions(data);

  const { questionIds, processedSetIds } = collectSetQuestions(data, setQuestionsMap, questionPositionMap);

  const finalQuestionIds = collectDefaultAndGroupQuestions(data, questionIds, processedSetIds, withSequentialDisplay);

  return sortQuestionsByPosition(finalQuestionIds, questionPositionMap);
}

// Создает карту позиций вопросов для правильной сортировки
function mapQuestionPositions(questionsIndices: string[] | undefined): Map<string, number> {
  const questionPositionMap = new Map<string, number>();

  if (!questionsIndices) return questionPositionMap;

  questionsIndices.forEach((questionId, index) => {
    questionPositionMap.set(questionId, index);
  });

  return questionPositionMap;
}

// Строит структуру сетов где хранятся все сеты для удобной навигации по ним
function mapSetQuestions(data: Poll): Map<string, { id: string; isInGroup: boolean; groupId?: string }[]> {
  const setQuestionsMap = new Map<string, { id: string; isInGroup: boolean; groupId?: string }[]>();
  if (!data.sets) return setQuestionsMap;

  data.sets.forEach(set => {
    const { id: setId } = set;
    if (!setQuestionsMap.has(setId)) {
      setQuestionsMap.set(setId, []);
    }

    // Добавляем все вопросы, принадлежащие этому сету
    const questionsInSet = data.questions.filter(q => q.setId === setId);
    questionsInSet.forEach(q => {
      setQuestionsMap.get(setId)?.push({ id: q.id, isInGroup: false });
    });

    // Добавляем первый вопрос каждой группы, связанной с этим сетом
    const groupsInSet = data.groups.filter(group => group.setId === setId);
    groupsInSet.forEach(group => {
      const groupQuestions = selectGroupQuestions(data, group.id);
      if (groupQuestions.length > 0 && groupQuestions[0]) {
        setQuestionsMap.get(setId)?.push({
          id: groupQuestions[0].id,
          isInGroup: true,
          groupId: group.id,
        });
      }
    });
  });

  return setQuestionsMap;
}

// Собирает вопросы из сетов с учетом ограничений pageLimit и групп
function collectSetQuestions(
  data: Poll,
  setQuestionsMap: Map<string, { id: string; isInGroup: boolean; groupId?: string }[]>,
  questionPositionMap: Map<string, number>,
): { questionIds: string[]; processedSetIds: Set<string> } {
  const questionIds: string[] = [];
  const processedSetIds = new Set<string>();
  const touchedGroups = new Set<string>();

  for (const [setId, questionObjectsInSet] of setQuestionsMap.entries()) {
    if (!processedSetIds.has(setId) && data.sets) {
      const set = data.sets.find(set => set.id === setId);
      const pageLimit = set?.data?.pageLimit;

      // Собираем вопросы для добавления, с учетом групп
      const questionsToAdd = gatherQuestionsToAdd(questionObjectsInSet, touchedGroups);

      // Сортируем вопросы по их позициям
      questionsToAdd.sort((a, b) => (questionPositionMap.get(a) ?? 0) - (questionPositionMap.get(b) ?? 0));

      // Применяем pageLimit, если он указан
      const limitedQuestions = pageLimit && pageLimit > 0 ? questionsToAdd.slice(0, pageLimit) : questionsToAdd;

      questionIds.push(...limitedQuestions);
      processedSetIds.add(setId);
    }
  }

  return { questionIds, processedSetIds };
}

// Собирает вопросы для добавления, добавляя только первый вопрос из каждой группы
function gatherQuestionsToAdd(
  questionObjectsInSet: { id: string; isInGroup: boolean; groupId?: string }[],
  touchedGroups: Set<string>,
): string[] {
  const questionsToAdd: string[] = [];

  questionObjectsInSet.forEach(questionObject => {
    const { id, isInGroup, groupId } = questionObject;
    if (isInGroup && groupId) {
      // Добавляем только первый вопрос из группы, если группа еще не обработана
      if (!touchedGroups.has(groupId)) {
        questionsToAdd.push(id);
        touchedGroups.add(groupId);
      }
    } else {
      questionsToAdd.push(id);
    }
  });

  return questionsToAdd;
}

function collectDefaultAndGroupQuestions(
  data: Poll,
  questionIds: string[],
  processedSetIds: Set<string>,
  withSequentialDisplay?: boolean,
): string[] {
  const touchedGroups = new Set<string>();

  data.questions.forEach(question => {
    if (question.setId) return;

    const groupId = getQuestionGroupId(question, data.questionDecks);

    if (groupId) {
      const group = selectGroup(data, groupId);
      const { hasSequentialDisplay } = group?.data ?? {};

      // Пропускаем группы, которые уже обработаны или связаны с сетом
      if (group?.setId && processedSetIds.has(group.setId)) return;

      // Добавляем вопрос, если группа еще не обработана или требуется последовательное отображение
      if (!touchedGroups.has(groupId) || (hasSequentialDisplay && withSequentialDisplay)) {
        questionIds.push(question.id);
      }

      touchedGroups.add(groupId);
    } else {
      questionIds.push(question.id);
    }
  });

  return questionIds;
}

// Сортирует вопросы по их позициям, чтобы сохранить исходный порядок
function sortQuestionsByPosition(questionIds: string[], questionPositionMap: Map<string, number>): string[] {
  return questionIds.sort((a, b) => (questionPositionMap.get(a) ?? 0) - (questionPositionMap.get(b) ?? 0));
}
